import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { Belt, SpinnerModal, Stepper } from 'components';
import { TextButton, Typography, Form, Grid, MenuItem, Box, TextField } from 'bonfire-kindling';
import { canadianProvinces, americanStates, countries } from 'constants/regions';
import { useTranslation, Trans } from 'react-i18next';
import CardLogos from 'assets/payment/4_Card_color_horizontal.svg';
import PoweredByStripe from 'assets/payment/Stripe.svg';

import useQuote from '../../hooks/useQuote';
import useRegistrationStepperProps from '../../../hooks/useRegistrationStepperProps';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const getPricingPlanDisplayName = ({ recurring = {} }) => {
  switch (recurring.interval) {
    case 'year':
      return 'Annual';
    case 'month':
      return 'Monthly';
    default:
      return '';
  }
};

const stripeElementOptions = {
  style: {
    base: {
      '::placeholder': {
        color: '#ccc',
      },
    },
    invalid: {
      color: '#c23d4b',
    },
  },
};
// Static function to use as inputComponent
const CardNumberWrapper = ({ onChange, ...props }) => (
  <CardNumberElement {...props} options={stripeElementOptions} />
);
const CardCvcWrapper = ({ onChange, ...props }) => (
  <CardCvcElement {...props} options={stripeElementOptions} />
);
const CardExpiryWrapper = ({ onChange, ...props }) => (
  <CardExpiryElement {...props} options={stripeElementOptions} />
);
const mapSubregion = ({ value, label }) => ({ value: value.split('.')[1], label });

const SubscriptionFormDesktop = ({
  isSubmitting,
  isLoadingDiscountCode,
  discountCode: code,
  validateDiscountCode,
  handleSubmit,
  pricingPlansIndexed,
  percentOff,
  taxRates,
  values,
}) => {
  const { t } = useTranslation();
  const registrationStepperProps = useRegistrationStepperProps();

  const { addressCountry, addressState, discountCode, selectedPlan } = values;
  const price = pricingPlansIndexed[selectedPlan] || {};

  const quote = useQuote({
    taxRates,
    billingInfo: {
      addressState,
    },
    price,
    percentOff,
  });
  const { total, subtotal, taxes, discount } = quote;

  const states = useMemo(() => {
    if (addressCountry === 'CA') {
      return canadianProvinces.map(mapSubregion);
    }
    if (addressCountry === 'US') {
      return americanStates.map(mapSubregion);
    }
    return [];
  }, [addressCountry]);

  return (
    <Box maxWidth="1050px" margin="auto" sx={{ padding: { xs: 2, md: 8 }, paddingTop: 8 }}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={4} width="100%">
          <Grid item xs={12}>
            <SpinnerModal open={isSubmitting} text={t('subscribe.finalizing')} />
            <Typography component="h1" variant="h1" style={{ textAlign: 'center' }}>
              {t('subscribe.subscribe_two_steps')}
            </Typography>

            <Typography
              variant="body1"
              component="body1"
              style={{
                textAlign: 'center',
                marginTop: '1rem',
                marginBottom: '1rem',
                fontWeight: 700,
              }}
            >
              <Trans i18nKey="subscribe.still_not_sure_preview">
                Still not sure?
                <RouterLink to="/preview">Preview our opportunities</RouterLink>
              </Trans>
            </Typography>
          </Grid>
          <Grid item xs={12} style={{ padding: '1.5rem', marginBottom: '1rem' }}>
            <Box display="flex" justifyContent="center">
              <Stepper {...registrationStepperProps} completeIndex={null} activeIndex={0} />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <div style={{ display: 'flex' }}>
              <div style={{ width: '100%' }}>
                <Typography variant="h3" component="h2">
                  {t('subscribe.account_info')}
                </Typography>
                <Form.TextField
                  name="email"
                  label={t('subscribe.email')}
                  required
                  fullWidth
                  margin="dense"
                />
                <Form.TextField
                  name="confirmEmail"
                  label={t('subscribe.confirm_email')}
                  required
                  fullWidth
                  margin="dense"
                />
              </div>
            </div>
            <div style={{ display: 'flex', marginTop: '2rem' }}>
              <div style={{ width: '100%' }}>
                <Typography variant="h3" component="h2" mb={2}>
                  {t('billing_info.title')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Form.SelectField
                      label={t('billing_info.country')}
                      name="addressCountry"
                      fullWidth
                      required
                      margin="none"
                      SelectDisplayProps={{ 'data-cy': 'addressCountry' }}
                    >
                      {countries.map(({ value, label }) => (
                        <MenuItem value={value} key={value} label={label} />
                      ))}
                    </Form.SelectField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Form.SelectField
                      label={t('billing_info.state_province')}
                      name="addressState"
                      required
                      disabled={!addressCountry}
                      SelectDisplayProps={{ 'data-cy': 'addressState' }}
                      fullWidth
                      margin="none"
                    >
                      {states.map(({ value, label }) => (
                        <MenuItem value={value} key={value} label={label} />
                      ))}
                    </Form.SelectField>
                  </Grid>
                  <Grid item xs={12}>
                    <Form.TextField
                      label={t('billing_info.address_line_1')}
                      name="addressLine1"
                      required
                      fullWidth
                      margin="none"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Form.TextField
                      label={t('billing_info.address_line_2')}
                      name="addressLine2"
                      fullWidth
                      margin="none"
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Form.TextField
                      label={t('billing_info.city')}
                      name="addressCity"
                      required
                      fullWidth
                      margin="none"
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Form.TextField
                      label={t('billing_info.zip')}
                      name="addressZip"
                      required
                      fullWidth
                      margin="none"
                    />
                  </Grid>
                </Grid>
              </div>
            </div>
            <div style={{ display: 'flex', marginTop: '2rem' }}>
              <div style={{ width: '100%' }}>
                <Typography variant="h4" component="h2" mb={2}>
                  {t('subscribe.payment_summary')}
                </Typography>
                <Belt style={{ alignItems: 'flex-end', marginBottom: '2em' }}>
                  <Box flexGrow={1} mr={3}>
                    <Form.TextField
                      name="discountCode"
                      label={t('subscribe.coupon')}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          validateDiscountCode(discountCode);
                        }
                      }}
                      fullWidth
                      margin="none"
                    />
                  </Box>
                  <TextButton
                    color="primary"
                    fullWidth
                    onClick={() => validateDiscountCode(discountCode)}
                    variant="contained"
                    isBusy={isLoadingDiscountCode}
                    style={{ width: '80px', border: 'none' }}
                    disabled={!discountCode}
                    label={t('subscribe.apply')}
                  />
                </Belt>
                <Belt>
                  <Typography variant="body2">
                    {t('subscribe.selected_plan', {
                      selectedPlan: getPricingPlanDisplayName(price),
                    }).toUpperCase()}
                  </Typography>
                  <Typography variant="body2">
                    {`${formatter.format(subtotal)} ${(price.currency || '').toUpperCase()}`}
                  </Typography>
                </Belt>
                {taxes.map(tax => (
                  <Belt key={tax.id}>
                    <Typography variant="body2">{tax.display_name}</Typography>
                    <Typography variant="body2">
                      {`${formatter.format(tax.amount)} ${(price.currency || '').toUpperCase()}`}
                    </Typography>
                  </Belt>
                ))}
                {!!code && (
                  <Belt>
                    <Typography variant="body2">{code}</Typography>
                    <Typography variant="body2">
                      {`- ${formatter.format(discount)} ${(price.currency || '').toUpperCase()}`}
                    </Typography>
                  </Belt>
                )}
                <Belt>
                  <Typography variant="body2">
                    <strong>{t('subscribe.total').toUpperCase()}</strong>
                  </Typography>
                  <Typography variant="body2">
                    <strong>
                      {`${formatter.format(total)} ${(price.currency || '').toUpperCase()}`}
                    </strong>
                  </Typography>
                </Belt>
              </div>
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box position="sticky" top="0" p={4} sx={{ bgcolor: 'blue.light' }}>
              <Typography variant="h3" component="h2">
                {t('subscribe.payment_method')}
              </Typography>
              <Belt style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                <img src={CardLogos} alt="Amex, Mastercard, Visa, Discover" height="40px" />
                <img src={PoweredByStripe} alt="Powered by Stripe" height="25px" />
              </Belt>
              <Typography variant="body3">{t('subscribe.canada_currency_message')}</Typography>
              <Form.TextField
                name="cardHolderName"
                label={t('subscribe.cardholder')}
                required
                fullWidth
              />
              <TextField
                label={t('subscribe.card_number')}
                InputProps={{
                  inputComponent: CardNumberWrapper,
                }}
                required
                fullWidth
              />
              <Belt mt={2}>
                <Box width="50%" pr={3}>
                  <TextField
                    label={t('subscribe.expiry')}
                    InputProps={{
                      inputComponent: CardExpiryWrapper,
                    }}
                    fullWidth
                    required
                  />
                </Box>
                <Box width="50%" pl={3}>
                  <TextField
                    label={t('subscribe.cvc')}
                    InputProps={{
                      inputComponent: CardCvcWrapper,
                    }}
                    fullWidth
                    required
                  />
                </Box>
              </Belt>
              <Box mt={3}>
                <TextButton
                  color="primary"
                  fullWidth
                  type="submit"
                  variant="contained"
                  isBusy={isSubmitting}
                  size="large"
                  label={t('subscribe.subscribe')}
                />
                <Typography
                  variant="caption"
                  style={{
                    padding: '1rem',
                    textAlign: 'center',
                    lineHeight: 1.66,
                  }}
                >
                  <Trans i18nKey="subscribe.subscribe_message">
                    {`By clicking "Subscribe", you are agreeing to Bonfire's`}
                    <a
                      href="https://gobonfire.com/terms-service/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>
                    {` and `}
                    <a
                      href="https://gobonfire.com/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </Trans>
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
export default SubscriptionFormDesktop;
